import {
  InitQuery,
  MeFragment,
  useInitQuery,
} from "@src/features/app/operations.generated";
import React, { useCallback, useEffect, useMemo } from "react";
import useSwitchOrgAndReload, {
  ORG_SWITCH_ATTEMPTED_KEY,
  ORG_SWITCH_CHANNEL,
  TAB_ID,
  formatOrgIdAndNameForUrl,
  getOrgIdAndNameFromUrl,
} from "@src/features/app/hooks/useSwitchOrgAndReload";
import { toast } from "sonner";
import { useRouter } from "next/router";

let hasHandledInitialOrgSync = false;

export const resetInitialOrgSyncForTesting = (): void => {
  hasHandledInitialOrgSync = false;
};

export interface UserContextState {
  user: MeFragment["user"] | undefined;
  isStaff: boolean;
}

const UserContext = React.createContext<UserContextState>({
  user: undefined,
  isStaff: false,
});

export const prepContext = (data: InitQuery): UserContextState => {
  return {
    user: data.Me?.user,
    isStaff: data.Me?.isStaff || false,
  };
};

export default UserContext;

export const useUserContextState = (): UserContextState => {
  const { switchOrgAndReload, syncOrgIdToUrl } = useSwitchOrgAndReload();
  const router = useRouter();

  const { data } = useInitQuery({
    onCompleted: async (queryData) => {
      if (hasHandledInitialOrgSync) return;
      hasHandledInitialOrgSync = true;

      const initOrgId = queryData.Me?.user?.org?.id;
      const initOrgName = queryData.Me?.user?.org?.name;
      if (!initOrgId || !initOrgName) return;
      const [urlOrgId, urlOrgName] = getOrgIdAndNameFromUrl();
      const attemptedOrgId = sessionStorage.getItem(ORG_SWITCH_ATTEMPTED_KEY);
      sessionStorage.removeItem(ORG_SWITCH_ATTEMPTED_KEY);
      if (urlOrgId && urlOrgName && urlOrgId !== initOrgId) {
        if (attemptedOrgId === String(urlOrgId)) {
          toast.error(
            `Couldn't switch organizations. You're still in ${initOrgName}.`,
          );
          await syncOrgIdToUrl(initOrgId, initOrgName);
        } else {
          const switched = await switchOrgAndReload(urlOrgId, urlOrgName);
          if (!switched) await syncOrgIdToUrl(initOrgId, initOrgName);
        }
      } else if (!urlOrgId || !urlOrgName) {
        await syncOrgIdToUrl(initOrgId, initOrgName);
      }
    },
  });

  const initOrgId = data?.Me?.user?.org?.id;
  const initOrgName = data?.Me?.user?.org?.name;
  const syncOrgIdToUrlOnNavigationComplete = useCallback(() => {
    const [urlOrgId, urlOrgName] = getOrgIdAndNameFromUrl();
    if (!initOrgId || !initOrgName) return;
    if (urlOrgId !== initOrgId || urlOrgName !== initOrgName) {
      syncOrgIdToUrl(initOrgId, initOrgName);
    }
  }, [initOrgId, initOrgName, syncOrgIdToUrl]);
  useEffect(() => {
    const channel = new BroadcastChannel(ORG_SWITCH_CHANNEL);
    channel.onmessage = (
      event: MessageEvent<{ orgId: number; orgName: string; tabId: string }>,
    ) => {
      if (event.data.tabId === TAB_ID) return;
      const [urlOrgId] = getOrgIdAndNameFromUrl();
      if (urlOrgId === event.data.orgId) return;
      sessionStorage.setItem(
        ORG_SWITCH_ATTEMPTED_KEY,
        String(event.data.orgId),
      );
      const url = new URL(window.location.href);
      url.searchParams.set(
        "org",
        formatOrgIdAndNameForUrl(event.data.orgId, event.data.orgName),
      );
      window.location.replace(url.toString());
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    router.events.on("routeChangeComplete", syncOrgIdToUrlOnNavigationComplete);
    return () =>
      router.events.off(
        "routeChangeComplete",
        syncOrgIdToUrlOnNavigationComplete,
      );
  }, [router.events, syncOrgIdToUrlOnNavigationComplete]);
  return useMemo(() => {
    if (!data) {
      return {
        user: undefined,
        isStaff: false,
      };
    }
    return prepContext(data);
  }, [data]);
};
